const normalizeVendorKey = (value) => String(value || '').trim().toLowerCase();

const toCleanText = (value) => {
  if (value == null) return '';
  return String(value).trim();
};

const safeLower = (value) => toCleanText(value).toLowerCase();

const isMergedVendor = (vendor) => {
  const status = safeLower(vendor?.status);
  const vendorStatus = safeLower(vendor?.vendor_status);
  return status === 'merged' || vendorStatus === 'merged';
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

// A vendor can receive an order only if at least one valid recipient email
// is configured. When the new send-to flags are explicit on the vendor row,
// the recipient list is the source of truth (so unticking both checkboxes
// disables sending even if contact_email is filled in). When the flags are
// missing entirely (legacy data), buildVendorContactInfo populates
// orderRecipientEmails from the legacy fallback chain, so the same check
// still works.
export const vendorCanReceiveOrder = (contact) => {
  const recipients = Array.isArray(contact?.orderRecipientEmails) ? contact.orderRecipientEmails : [];
  return recipients.some(isValidEmail);
};

// Resolve which email addresses purchase orders should be sent to, based on
// the per-vendor "send orders here" flags. If neither flag is explicitly set,
// fall back to the legacy behavior (contact_email -> general_email) so vendors
// created before this feature keep working without configuration.
const resolveOrderRecipients = (vendor) => {
  const contactEmail = toCleanText(vendor?.contact_email);
  const generalEmail = toCleanText(vendor?.general_email);
  const sendToContact = vendor?.send_orders_to_contact_email;
  const sendToGeneral = vendor?.send_orders_to_general_email;
  const flagsExplicit = sendToContact !== undefined && sendToContact !== null
    && sendToGeneral !== undefined && sendToGeneral !== null;

  const recipients = [];
  if (flagsExplicit) {
    if (sendToContact && isValidEmail(contactEmail)) recipients.push(contactEmail);
    if (sendToGeneral && isValidEmail(generalEmail)) recipients.push(generalEmail);
  } else {
    // Legacy fallback: prefer contact email, then general email.
    if (isValidEmail(contactEmail)) recipients.push(contactEmail);
    else if (isValidEmail(generalEmail)) recipients.push(generalEmail);
  }
  // De-dupe (case-insensitive) preserving order.
  const seen = new Set();
  return recipients.filter((email) => {
    const key = email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const buildVendorContactInfo = (vendor) => {
  const contactName = toCleanText(vendor?.contact_name);
  const orderRecipientEmails = resolveOrderRecipients(vendor);
  // contactEmail is the "primary recipient" used in PO headers, previews,
  // and email send paths. When the new send-to flags exist on the vendor
  // row, it is the first selected recipient (or empty if none ticked, so
  // sending is correctly blocked). When the flags are missing (legacy
  // data), resolveOrderRecipients returned the legacy fallback so this
  // resolves to the same value as before.
  const contactEmail = orderRecipientEmails[0] || '';
  const contactPhone = toCleanText(vendor?.contact_phone) || toCleanText(vendor?.general_phone);

  return {
    contactName,
    contactEmail,
    contactPhone,
    secondaryContactName: toCleanText(vendor?.contact2_name),
    secondaryContactEmail: toCleanText(vendor?.contact2_email),
    secondaryContactPhone: toCleanText(vendor?.contact2_phone),
    generalEmail: toCleanText(vendor?.general_email),
    generalPhone: toCleanText(vendor?.general_phone),
    orderingMethod: toCleanText(vendor?.ordering_method),
    website: toCleanText(vendor?.website),
    address: toCleanText(vendor?.address),
    sendOrdersToContactEmail: vendor?.send_orders_to_contact_email !== false,
    sendOrdersToGeneralEmail: vendor?.send_orders_to_general_email === true,
    orderRecipientEmails,
  };
};

const rememberVendorKey = (keyMap, key, vendor) => {
  const normalized = normalizeVendorKey(key);
  if (!normalized || !vendor) return;
  if (!keyMap.has(normalized)) keyMap.set(normalized, vendor);
};

const getPreferredVendorSelect = () => (
  'id, display_name, original_name, status, vendor_status,'
  + ' contact_name, contact_email, contact_phone,'
  + ' contact2_name, contact2_email, contact2_phone,'
  + ' general_phone, general_email, ordering_method, website, address,'
  + ' send_orders_to_contact_email, send_orders_to_general_email'
);

async function loadVendorsWithFallback(supabaseClient, storeId) {
  const preferredSelect = getPreferredVendorSelect();
  const fallbackSelect = 'id, display_name, status, contact_name, contact_email, contact_phone, general_phone, general_email';

  const preferred = await supabaseClient
    .from('vendors')
    .select(preferredSelect)
    .eq('store_id', storeId);

  if (!preferred.error) return preferred.data || [];

  const fallback = await supabaseClient
    .from('vendors')
    .select(fallbackSelect)
    .eq('store_id', storeId);

  if (fallback.error) throw fallback.error;
  return fallback.data || [];
}

export async function loadVendorDirectory(supabaseClient, storeId) {
  if (!supabaseClient || !storeId) {
    return { vendorsById: new Map(), vendorsByKey: new Map() };
  }

  try {
    const vendors = await loadVendorsWithFallback(supabaseClient, storeId);
    const activeVendors = vendors.filter((vendor) => !isMergedVendor(vendor));

    const vendorsById = new Map();
    const vendorsByKey = new Map();

    activeVendors.forEach((vendor) => {
      vendorsById.set(vendor.id, vendor);
      rememberVendorKey(vendorsByKey, vendor.display_name, vendor);
      rememberVendorKey(vendorsByKey, vendor.original_name, vendor);
    });

    try {
      const aliasResponse = await supabaseClient
        .from('vendor_aliases')
        .select('vendor_id, alias')
        .eq('store_id', storeId);

      if (!aliasResponse.error) {
        (aliasResponse.data || []).forEach((aliasRow) => {
          const vendor = vendorsById.get(aliasRow.vendor_id);
          if (!vendor) return;
          rememberVendorKey(vendorsByKey, aliasRow.alias, vendor);
        });
      }
    } catch (aliasError) {
      // Alias table is optional in some environments.
      console.warn('Vendor alias lookup unavailable:', aliasError?.message || aliasError);
    }

    return { vendorsById, vendorsByKey };
  } catch (error) {
    console.warn('Could not load vendor directory:', error?.message || error);
    return { vendorsById: new Map(), vendorsByKey: new Map() };
  }
}

export function resolveVendorForName(vendorName, vendorDirectory) {
  const normalized = normalizeVendorKey(vendorName);
  if (!normalized) return null;
  return vendorDirectory?.vendorsByKey?.get(normalized) || null;
}

export function getVendorSendReadiness(contactInfo, options = {}) {
  const vendorAssigned = options?.vendorAssigned !== false;
  if (!vendorAssigned) {
    return {
      canSend: false,
      code: 'no_vendor_assigned',
      reason: 'Not sent — no vendor assigned. Use Items + AI to assign vendors.',
    };
  }

  if (vendorCanReceiveOrder(contactInfo)) {
    return { canSend: true, code: 'ready', reason: '' };
  }

  return {
    canSend: false,
    code: 'vendor_missing_email',
    reason: 'Not sent — vendor has no contact email. Add an email, or mark as sent outside Countertop.',
  };
}

export function normalizeVendorLabel(value) {
  const clean = toCleanText(value);
  return clean || 'Unknown Vendor';
}
